import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable, tap } from 'rxjs';

export interface LoginRequest {
  password: string;
  host?: string; // The host we are trying to access
}

export interface LoginResponse {
  message?: string;
  host?: string;
  token?: string;
  cookieDomain?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  login(password: string, host?: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/auth/login`, { password, host }, {
      withCredentials: true // Important for setting cookies
    }).pipe(
      tap((response) => this.writeFirstPartyCookieFallback(response, host))
    );
  }

  verify(): Observable<any> {
    return this.http.get(`${this.apiUrl}/auth/verify`, {
      withCredentials: true
    });
  }

  private writeFirstPartyCookieFallback(response: LoginResponse, host?: string) {
    const token = response?.token;
    const cookieDomain = response?.cookieDomain;

    if (!token || !cookieDomain) {
      return;
    }

    // Only write a client-side cookie if the cookie domain actually matches the
    // target host (otherwise the browser will drop it and we leak the token).
    const targetHost = host || window.location.hostname;
    if (!this.hostMatchesCookieDomain(targetHost, cookieDomain)) {
      return;
    }

    const maxAge = 3600 * 24; // 24 hours
    document.cookie = `access_token=${token}; Domain=${cookieDomain}; Path=/; Secure; SameSite=Lax; Max-Age=${maxAge}`;
  }

  private hostMatchesCookieDomain(host: string, cookieDomain: string): boolean {
    const normalizedDomain = cookieDomain.startsWith('.') ? cookieDomain.slice(1) : cookieDomain;
    return host === normalizedDomain || host.endsWith(`.${normalizedDomain}`);
  }
}
